import Breadcrumbs from '../components/Breadcrumbs';
import { CONTENT_CLASSES } from '../constants/classNames';
import { CONTACT_EMAIL, IS_SUPPORT_BUTTON_VISIBLE, ROUTES, SITE_NAME, SITE_DOMAIN } from '../constants/config';
import '../styles/ContentPage.css';

// Update this whenever the policy changes.
const POLICY_UPDATED = '30 September 2026';

function PolicySection({ title, children }) {
  return (
    <section className={CONTENT_CLASSES.section}>
      <h2 className={CONTENT_CLASSES.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

/**
 * Privacy policy written for a site used by children. Must match what the site really does:
 * update it if analytics, accounts or new services are added.
 */
export default function PrivacyPage() {
  return (
    <article className={CONTENT_CLASSES.root}>
      <Breadcrumbs
        trail={[
          { label: 'Lab', path: ROUTES.LAB },
          { label: 'Privacy policy', path: ROUTES.PRIVACY },
        ]}
      />
      <header className={CONTENT_CLASSES.hero}>
        <h1 className={CONTENT_CLASSES.heading}>Privacy policy</h1>
        <p className={CONTENT_CLASSES.meta}>Last updated: {POLICY_UPDATED}</p>
        <p className={CONTENT_CLASSES.lead}>
          {SITE_NAME} ({SITE_DOMAIN}) is made for children. We collect as little as possible, and we never ask
          anyone for their name, email address or any other personal details.
        </p>
      </header>

      <PolicySection title="What we don't collect">
        <p className={CONTENT_CLASSES.text}>
          There are no accounts, sign-ups, chats or forms on {SITE_NAME}. We don't ask for or store names, email
          addresses, photos, locations or any other personal information from children or adults.
        </p>
      </PolicySection>

      <PolicySection title="What is stored on your device">
        <p className={CONTENT_CLASSES.text}>
          We save one setting in your browser's local storage: whether "Read to me" is switched on or off. It never
          leaves your device, and you can clear it at any time by clearing your browser's site data.
        </p>
      </PolicySection>

      <PolicySection title="Advertising">
        <p className={CONTENT_CLASSES.text}>
          To keep {SITE_NAME} free, we may show ads from Google AdSense. Because our site is directed at children,
          every ad request is tagged for child-directed treatment. This tells Google not to show interest-based
          (personalised) ads or remarketing ads, in line with the US Children's Online Privacy Protection Act (COPPA)
          and similar laws. Google may still use cookies or similar technology for things like limiting how often
          an ad is shown and preventing fraud. You can read how Google uses information at{' '}
          <a href="https://policies.google.com/technologies/partner-sites" rel="noopener noreferrer" target="_blank">
            policies.google.com/technologies/partner-sites
          </a>
          .
        </p>
        <p className={CONTENT_CLASSES.text}>
          Visitors in the European Economic Area, the UK and Switzerland are asked for their consent choices before
          any cookies are used for advertising.
        </p>
      </PolicySection>

      <PolicySection title="Services that help run the site">
        <p className={CONTENT_CLASSES.text}>
          Like every website, our hosting provider keeps short-lived technical logs (such as IP addresses and the
          pages requested) to deliver the site and protect it from attacks. Our fonts are loaded from Google Fonts,
          which receives your IP address when the font is downloaded. Fizzy's voice is pre-recorded and played from
          our own site; nothing you do is sent to a voice service.
        </p>
      </PolicySection>

      <PolicySection title="Children's privacy">
        <p className={CONTENT_CLASSES.text}>
          We do not knowingly collect personal information from children under 13 (or the age required in your
          country). If you are a parent or guardian and believe your child has sent us personal information, please
          email us and we will delete it.
        </p>
      </PolicySection>

      <PolicySection title="Links to other sites">
        <p className={CONTENT_CLASSES.text}>
          {IS_SUPPORT_BUTTON_VISIBLE ? 'Our Support Us button and a few links take' : 'A few links take'} you to other websites, which have their own privacy policies. We
          recommend a grown-up checks them first.
        </p>
      </PolicySection>

      <PolicySection title="Contact us">
        <p className={CONTENT_CLASSES.text}>
          Questions about privacy? Email us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. If we change
          this policy we will update the date at the top of this page.
        </p>
      </PolicySection>
    </article>
  );
}
